'use client';

import { useEffect, useRef } from 'react';

/**
 * CirculationDiagram —「ご縁と学びの循環」を可視化する円環図
 *
 * 目的:
 * - ミッションの骨格「繋がる → 広がる → ともに高めあう」を、並列3カラムではなく
 *   閉じた輪（循環）として示し、構造そのものをデザインで語る。
 *
 * 方針:
 * - 中心に「縁」。弁柄（単一アクセント）の進行アークがスクロールで描かれる。
 * - 色はトークン駆動（stroke-primary=弁柄 / stroke-line / fill-*）。
 * - prefers-reduced-motion を尊重（アニメーションせず最初から完成形）。
 */
const CX = 170;
const CY = 170;
const R = 118;
const CIRC = 2 * Math.PI * R;

const NODES = [
  { a: -90, jp: '繋がる', en: '01' },
  { a: 30, jp: '広がる', en: '02' },
  { a: 150, jp: 'ともに高めあう', en: '03' },
];

export default function CirculationDiagram() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const arcRef = useRef<SVGCircleElement>(null);

  useEffect(() => {
    const arc = arcRef.current;
    if (!arc) return;
    const reduce = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (reduce) {
      arc.style.strokeDashoffset = '0';
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            requestAnimationFrame(() => {
              arc.style.strokeDashoffset = '0';
            });
            io.disconnect();
          }
        });
      },
      { threshold: 0.3 },
    );
    if (wrapRef.current) io.observe(wrapRef.current);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={wrapRef}
      className='relative mx-auto aspect-square w-full max-w-[360px]'
    >
      <svg
        viewBox='0 0 340 340'
        className='h-full w-full overflow-visible'
        role='img'
        aria-label='繋がる・広がる・ともに高めあう の循環図'
      >
        {/* 破線の輪（下地）*/}
        <circle
          cx={CX}
          cy={CY}
          r={R}
          strokeWidth={1.4}
          strokeDasharray='2 7'
          strokeLinecap='round'
          className='fill-none stroke-line'
        />
        {/* 進行アーク（弁柄・スクロールで描画）*/}
        <circle
          ref={arcRef}
          cx={CX}
          cy={CY}
          r={R}
          strokeWidth={2.4}
          strokeLinecap='round'
          strokeDasharray={CIRC}
          transform={`rotate(-90 ${CX} ${CY})`}
          className='fill-none stroke-primary'
          style={{
            strokeDashoffset: CIRC,
            transition: 'stroke-dashoffset 1.6s cubic-bezier(.4,0,.2,1)',
          }}
        />
        {/* 中心 */}
        <text
          x={CX}
          y={CY - 2}
          textAnchor='middle'
          dominantBaseline='middle'
          className='fill-primary font-serif'
          style={{ fontSize: 40 }}
        >
          縁
        </text>
        <text
          x={CX}
          y={CY + 26}
          textAnchor='middle'
          className='fill-[color:var(--color-tertiary)]'
          style={{ fontSize: 10, letterSpacing: '.24em', fill: '#8b7f70' }}
        >
          CIRCULATION
        </text>
        {/* ノード */}
        {NODES.map((n) => {
          const rad = (n.a * Math.PI) / 180;
          const x = CX + Math.cos(rad) * R;
          const y = CY + Math.sin(rad) * R;
          const lx = CX + Math.cos(rad) * (R + 34);
          const ly = CY + Math.sin(rad) * (R + 34);
          const anchor =
            Math.abs(Math.cos(rad)) < 0.3
              ? 'middle'
              : Math.cos(rad) > 0
                ? 'start'
                : 'end';
          return (
            <g key={n.jp}>
              <circle
                cx={x}
                cy={y}
                r={15}
                strokeWidth={2}
                className='fill-paper stroke-primary'
              />
              <circle cx={x} cy={y} r={5.5} className='fill-primary' />
              <text
                x={lx}
                y={ly - 18}
                textAnchor={anchor}
                className='fill-primary'
                style={{ fontSize: 11, letterSpacing: '.1em' }}
              >
                {n.en}
              </text>
              <text
                x={lx}
                y={ly}
                textAnchor={anchor}
                dominantBaseline='middle'
                className='fill-ink font-serif'
                style={{ fontSize: 15, letterSpacing: '.05em' }}
              >
                {n.jp}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
